import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@packages/api';

export class ThumbnailGeneratingError extends Error {
  constructor() {
    super('Thumbnail is generating');
    this.name = 'ThumbnailGeneratingError';
  }
}

class FileNotFoundError extends Error {
  constructor(status: number) {
    super(`File not found: ${status}`);
    this.name = 'FileNotFoundError';
  }
}

export async function downloadFileContent(domainId: number, fileId: string): Promise<Blob> {
  const res = await apiFetch(`/storage/v1/domains/${domainId}/files/${fileId}/download`);
  if (!res.ok) throw new Error(`Download failed: ${res.status}`);
  return res.blob();
}

async function downloadFileThumbnail(domainId: number, fileId: string): Promise<Blob> {
  const res = await apiFetch(`/storage/v1/domains/${domainId}/files/${fileId}/thumbnail/download?size=medium`);
  if (res.ok) return res.blob();

  if (res.status === 400 || res.status === 404) {
    // Check if the file itself exists via issueFileDownload
    const checkRes = await apiFetch(`/storage/v1/domains/${domainId}/files/${fileId}/issueDownload`, {
      method: 'POST',
      body: JSON.stringify({ mode: 'ticket' }),
    });
    if (checkRes.ok) {
      // File exists but thumbnail not ready yet
      throw new ThumbnailGeneratingError();
    }
    // File doesn't exist — no point retrying
    throw new FileNotFoundError(res.status);
  }

  throw new Error(`Thumbnail download failed: ${res.status}`);
}

async function fetchStreamUrl(domainId: number, fileId: string): Promise<string> {
  const res = await apiFetch(`/storage/v1/domains/${domainId}/files/${fileId}/issueDownload`, {
    method: 'POST',
    body: JSON.stringify({ mode: 'ticket' }),
  });
  if (!res.ok) throw new Error(`Stream URL failed: ${res.status}`);
  const data = await res.json();
  return data.url;
}

export function useResourceThumbnail(domainId: number, fileId: string | undefined) {
  return useQuery({
    queryKey: ['resourceThumbnail', domainId, fileId],
    queryFn: async () => {
      const blob = await downloadFileThumbnail(domainId, fileId!);
      return URL.createObjectURL(blob);
    },
    enabled: !!fileId && domainId > 0,
    staleTime: 30 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error instanceof FileNotFoundError) return false;
      if (error instanceof ThumbnailGeneratingError) return failureCount < 10;
      return failureCount < 2;
    },
    retryDelay: (_failureCount, error) => {
      if (error instanceof ThumbnailGeneratingError) return 3000;
      return 1000;
    },
  });
}

export function useResourceFullSize(domainId: number, fileId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['resourceFullSize', domainId, fileId],
    queryFn: async () => {
      const blob = await downloadFileContent(domainId, fileId!);
      return URL.createObjectURL(blob);
    },
    enabled: enabled && !!fileId && domainId > 0,
    staleTime: Infinity,
  });
}

export function useVideoStreamUrl(domainId: number, fileId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['videoStreamUrl', domainId, fileId],
    queryFn: () => fetchStreamUrl(domainId, fileId!),
    enabled: enabled && !!fileId && domainId > 0,
    staleTime: 30 * 60 * 1000,
  });
}
